import { NormalizedGroup } from '../../../redux/groups/api/GroupAdapter';
import {
  getDictionaryGroups,
  getGroupByIdWithFallbackSelector,
  getGroupsByWorkspaceIdSelector,
} from '../../../redux/groups/selectors';
import { useAppSelector } from '../../../redux/store.model';
import { useMemo } from 'react';
import { GroupType } from '../redux/group/model';
import { getMyWorkspaceSelector } from '../../../redux/stoerkID/selectors/StoerkId.selectors';

export const useGetChildCount = (group: NormalizedGroup) => {
  const workspace = useAppSelector(getMyWorkspaceSelector);
  const regularGroupsById = useAppSelector(getDictionaryGroups);
  const { data: workspaceGroupsById } = useAppSelector((state) =>
    getGroupsByWorkspaceIdSelector(state, { workspaceId: workspace?.id ?? '' })
  );
  const groupByIdWithFallback = useAppSelector((state) =>
    getGroupByIdWithFallbackSelector(state, {
      groupId: group?.id,
      workspaceId: workspace?.id ?? '',
    })
  );

  const { storesCount, merchandisersCount } = useMemo(() => {
    // Merge both caches so recursive child lookups always resolve
    const groupsById = {
      ...regularGroupsById,
      ...workspaceGroupsById?.entities.groups,
    };
    const rootGroup = groupByIdWithFallback ?? groupsById[group.id] ?? group;
    const visited = new Set<string>();

    const countChildrenRecursively = (currentGroup: NormalizedGroup) => {
      if (visited.has(currentGroup.id)) {
        return { storesCount: 0, merchandisersCount: 0 };
      }

      visited.add(currentGroup.id);

      let stores = 0;
      let merchandisers = 0;

      for (const childGroupId of currentGroup.childGroups ?? []) {
        const childGroup = groupsById[childGroupId];
        if (!childGroup) continue;
        const businessRole =
          childGroup.attributes?.BUSINESS_ROLE === GroupType.Store;

        if (businessRole) {
          stores += 1;
        } else if (childGroup.composableDevice !== undefined) {
          merchandisers += 1;
        }

        const childCounts = countChildrenRecursively(childGroup);
        stores += childCounts.storesCount;
        merchandisers += childCounts.merchandisersCount;
      }

      return { storesCount: stores, merchandisersCount: merchandisers };
    };

    return countChildrenRecursively(rootGroup);
  }, [regularGroupsById, workspaceGroupsById, group, groupByIdWithFallback]);

  return {
    storesCount,
    merchandisersCount,
  };
};
